import React, { useEffect, useState } from 'react';
import { Star, ExternalLink } from 'lucide-react';
import { Reveal } from '../hooks/useReveal';

// Avis Google Maps — alimenté par /reviews.json (généré côté serveur via
// tools/fetch_google_reviews.py + cron). Dégradation gracieuse : si le fichier
// est absent ou vide, la section ne s'affiche pas (zéro UI morte).
function Stars({ n = 5 }) {
  return (
    <div className="flex gap-0.5" aria-label={`${n} sur 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={15} className={i < Math.round(n) ? 'text-accent fill-accent' : 'text-[#333]'} />
      ))}
    </div>
  );
}

export default function GoogleReviews() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch('/reviews.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setData(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const reviews = data?.reviews || [];
  if (!reviews.length) return null; // rien à montrer → section masquée

  const summary = data?.summary; // { rating, total, url }
  return (
    <section className="py-16 md:py-28 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal className="text-center mb-12">
          <p className="hx-eyebrow mb-4">Avis vérifiés</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95]">Nos avis Google</h2>
          {summary && (
            <div className="flex items-center justify-center gap-3 mt-5">
              <Stars n={summary.rating} />
              <span className="text-white font-cond text-lg">{Number(summary.rating).toFixed(1)}/5</span>
              {summary.total != null && (
                <span className="text-[var(--hx-muted)] text-sm">· {summary.total} avis</span>
              )}
              {summary.url && (
                <a href={summary.url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 text-accent text-sm hover:underline">
                  Voir sur Google <ExternalLink size={13} />
                </a>
              )}
            </div>
          )}
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {reviews.slice(0, 6).map((r, i) => (
            <Reveal key={`${r.author}-${i}`} delay={(i % 3) * 120}>
              <div className="hx-card rounded-md p-7 h-full flex flex-col">
                <Stars n={r.rating} />
                <p className="text-[var(--hx-text)] leading-relaxed my-5 flex-1">"{r.text}"</p>
                <div className="flex items-center gap-3">
                  {r.photo
                    ? <img src={r.photo} alt={r.author} loading="lazy" referrerPolicy="no-referrer" className="w-11 h-11 rounded-full object-cover" />
                    : <span className="w-11 h-11 rounded-full bg-accent/15 border border-accent/30 grid place-items-center text-accent font-cond">{(r.author || '?').charAt(0)}</span>}
                  <div>
                    <p className="font-cond tracking-wide">{r.author}</p>
                    {(r.club || r.time) && <p className="text-[var(--hx-muted)] text-xs">{[r.club, r.time].filter(Boolean).join(' · ')}</p>}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
